import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export interface AdminFormToast {
  variant: "success" | "danger";
  text: string;
}

/**
 * Shared post-save plumbing for admin form pages.
 *
 * - Picks up an `AdminFormToast` from `location.state.toast` on mount (so a
 *   New page can `navigate(..., { state: { toast } })` to its Edit page and
 *   have the toast appear there).
 * - Clears that router state on mount so a browser reload doesn't show the
 *   toast again.
 * - Tracks `formKey` + `savedInitial` so an Edit page can call `markSaved`
 *   after a successful update to (a) reset the form's internal dirty state
 *   by remounting via `key` and (b) seed the next mount's `initialValues`
 *   with the just-saved values, so the form doesn't briefly re-render from
 *   stale RTK Query cache.
 */
export const useAdminFormPersistence = <V>() => {
  const location = useLocation();
  const initialToast = (location.state as { toast?: AdminFormToast } | null)
    ?.toast;

  const [toast, setToast] = useState<AdminFormToast | null>(
    initialToast ?? null
  );
  const [savedInitial, setSavedInitial] = useState<V | null>(null);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (initialToast) {
      // Drop the state so reload doesn't replay the toast.
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSaved = (values: V, text: string) => {
    setSavedInitial(values);
    setFormKey((k) => k + 1);
    setToast({ variant: "success", text });
  };

  return { toast, setToast, savedInitial, formKey, markSaved };
};
